import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { FileXls } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

export default function StockSummaryPage() {
  const [asOn, setAsOn] = useState(today());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/inventory/stock-summary", { params: { as_on: asOn } }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  }, [asOn]);
  useEffect(() => { load(); }, [load]);
  const [sjOpen, setSjOpen] = useState(false);

  const downloadXlsx = async () => {
    try {
      const res = await fetch(`${API}/inventory/stock-summary.xlsx?as_on=${asOn}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `stock-summary-${asOn}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="stock-summary-page">
      <PageHeader title="Stock Summary" subtitle="Item-wise quantity flow and stock valuation, like Tally's Stock Summary." />
      <div className="p-8 space-y-6">
        <div className="card-base p-4 flex items-end gap-3 flex-wrap">
          <div><div className="label-overline mb-1">As on</div><input type="date" className="field" value={asOn} onChange={e => setAsOn(e.target.value)} data-testid="ss-ason" /></div>
          <button onClick={load} className="btn-primary" data-testid="ss-run">Show Stock Summary</button>
          <button onClick={downloadXlsx} disabled={!data} className="btn-secondary inline-flex items-center gap-1.5" data-testid="ss-xlsx"><FileXls size={14} weight="bold" /> Download Excel</button>
          <button onClick={() => setSjOpen(true)} className="btn-primary inline-flex items-center gap-1.5" data-testid="sj-open">+ Stock Journal</button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
              <Kpi label="Stock Value" value={fmtINR(data.totals.closing_value)} accent />
              <Kpi label="Items" value={data.totals.items} />
              <Kpi label="Low Stock" value={data.totals.low_stock} warn={data.totals.low_stock > 0} testId="ss-low-stock-kpi" />
              <Kpi label="Negative Stock" value={data.totals.negative_stock} warn={data.totals.negative_stock > 0} />
            </div>
            <div className="table-wrapper">
              <table className="table-base">
                <thead><tr><th>Item</th><th>HSN</th><th className="text-right">Opening</th><th className="text-right">Inward</th><th className="text-right">Outward</th><th className="text-right">Closing</th><th className="text-right">Rate</th><th className="text-right">Value</th></tr></thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-ink-muted">No products yet.</td></tr>}
                  {data.rows.map((r, i) => (
                    <tr key={r.id} data-testid={`ss-row-${i}`}>
                      <td className="font-semibold">{r.name}<span className="text-[10px] text-ink-muted ml-1">{r.unit}</span>
                        {r.low_stock && <span className="status-pill status-warning ml-2" data-testid={`ss-low-${i}`}>LOW — min {r.min_stock}</span>}
                      </td>
                      <td className="font-mono text-xs">{r.hsn || "—"}</td>
                      <td className="text-right tabular">{r.opening}</td>
                      <td className="text-right tabular text-brand">{r.inward > 0 ? `+${r.inward}` : ""}</td>
                      <td className="text-right tabular text-destructive">{r.outward > 0 ? `−${r.outward}` : ""}</td>
                      <td className={`text-right tabular font-bold ${r.closing < 0 ? "text-destructive" : ""}`}>{r.closing}</td>
                      <td className="text-right tabular">{fmtINR(r.rate)}</td>
                      <td className="text-right tabular font-semibold">{fmtINR(r.value)}</td>
                    </tr>
                  ))}
                  {data.rows.length > 0 && (
                    <tr className="bg-surface-muted font-bold" data-testid="ss-total-row">
                      <td colSpan={7}>Total Stock Value</td>
                      <td className="text-right tabular">{fmtINR(data.totals.closing_value)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {sjOpen && <StockJournalModal onClose={() => setSjOpen(false)} onDone={() => { setSjOpen(false); load(); }} />}
      <FieldStyle />
    </div>
  );
}

function StockJournalModal({ onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const newSjLine = () => ({ _uid: Math.random().toString(36).slice(2), product_id: "", qty: "", reason: "" });
  const [lines, setLines] = useState([newSjLine()]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data)); }, []);
  const setLine = (i, patch) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  const save = async () => {
    const valid = lines.filter(l => l.product_id && Number(l.qty));
    if (!valid.length) { toast.error("Add a product and quantity (use negative to reduce stock)"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/stock-journal", { date, notes, lines: valid.map(l => ({ product_id: l.product_id, qty: Number(l.qty), reason: l.reason })) });
      toast.success("Stock journal posted — summary updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="sj-modal">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-xl space-y-3 max-h-[85vh] overflow-auto">
        <div className="font-display font-semibold">Stock Journal (adjustment / transfer)</div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="label-overline mb-1">Date</div><input type="date" className="field" value={date} onChange={e => setDate(e.target.value)} data-testid="sj-date" /></div>
          <div><div className="label-overline mb-1">Notes</div><input className="field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Physical stock verification" data-testid="sj-notes" /></div>
        </div>
        <div className="text-[11px] text-ink-muted">Positive qty = add stock (inward) · Negative qty = reduce stock (outward/shortage)</div>
        {lines.map((l, i) => (
          <div key={l._uid} className="flex items-center gap-2">
            <select className="field flex-1" value={l.product_id} onChange={e => setLine(i, { product_id: e.target.value })} data-testid={`sj-product-${i}`}>
              <option value="">Select product…</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="±Qty" className="field !w-24 text-right" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} data-testid={`sj-qty-${i}`} />
            <input className="field !w-40" placeholder="Reason" value={l.reason} onChange={e => setLine(i, { reason: e.target.value })} data-testid={`sj-reason-${i}`} />
          </div>
        ))}
        <button onClick={() => setLines(ls => [...ls, newSjLine()])} className="btn-ghost text-xs" data-testid="sj-add-line">+ Add line</button>
        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <button onClick={onClose} className="btn-secondary" data-testid="sj-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="sj-save">{saving ? "Posting…" : "Post Journal"}</button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, warn, testId }) {
  return (
    <div className={`card-base p-3 ${accent ? "bg-brand text-white" : warn ? "bg-destructive text-white" : ""}`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-wide ${accent || warn ? "text-white/70" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display text-lg font-bold tabular">{value}</div>
    </div>
  );
}
